import React, { useState, useEffect } from 'react';
import { updateMyProfile, changePassword, uploadProfilePhoto, deleteProfilePhoto, getMyProfile } from '../services/usersAPI';
import '../styles/Profile.css';
import { useToast } from './notifications/ToastProvider';
import { buildImageUrl } from '../utils/urlHelpers';

const DEFAULT_AVATAR = '/default-avatar.png';

const getImageUrl = (imagePath) => buildImageUrl(imagePath, { defaultPath: DEFAULT_AVATAR });

function Profile({ userRole, userName, onPhotoUpdate }) {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  
  // État pour les informations du profil
  const [profile, setProfile] = useState({
    photo: DEFAULT_AVATAR,
    nom: '',
    prenom: '',
    role: '',
    email: '',
    telephone: '',
    bureau: '',
    username: '',
    statut: 'actif'
  });

  // États pour les modales de confirmation
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalData, setConfirmModalData] = useState({
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null
  });

  // Effet pour mettre à jour le profil avec les props
  useEffect(() => {
    const loadProfile = async () => {
      try {
        console.log('🔄 Chargement du profil depuis l\'API...');
        const profileData = await getMyProfile();
        
        console.log('📥 Profil reçu de l\'API:', profileData);
        
        if (profileData.success && profileData.data) {
          setProfile(prevProfile => ({
            ...prevProfile,
            ...profileData.data,
            role: profileData.data.role || userRole || 'N/A',
            username: `${profileData.data.prenom} ${profileData.data.nom}` || userName || 'N/A'
          }));
        } else {
          console.log('⚠️ Fallback vers données simulées');
          const userDetails = {
            nom: userName ? userName.split(' ').slice(1).join(' ') : 'N/A',
            prenom: userName ? userName.split(' ')[0] : 'N/A',
            email: `${userName ? userName.toLowerCase().replace(' ', '.') : 'utilisateur'}@example.com`,
          };

          setProfile(prevProfile => ({
            ...prevProfile,
            ...userDetails,
            role: userRole || 'N/A',
            username: userName || 'N/A'
          }));
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement du profil:', error);
        const userDetails = {
          nom: userName ? userName.split(' ').slice(1).join(' ') : 'N/A',
          prenom: userName ? userName.split(' ')[0] : 'N/A', 
          email: `${userName ? userName.toLowerCase().replace(' ', '.') : 'utilisateur'}@example.com`,
        };

        setProfile(prevProfile => ({
          ...prevProfile,
          ...userDetails,
          role: userRole || 'N/A',
          username: userName || 'N/A'
        }));
      }
    };

    loadProfile();
  }, [userRole, userName]);

  // État pour le mode édition
  const [editMode, setEditMode] = useState(false);
  
  // Fonction pour vérifier si l'utilisateur peut modifier les informations de base
  const canEditBasicInfo = () => {
    return userRole === 'administrateur' || userRole === 'admin' || userRole === 'directeur';
  };
  
  // État pour le changement de mot de passe
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // État pour l'upload de fichier
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  // Gérer le changement de valeurs du profil
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prevProfile => ({
      ...prevProfile,
      [name]: value
    }));
  };

  // Gérer le changement des valeurs du mot de passe
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prevData => ({
      ...prevData,
      [name]: value
    }));
  };

  // Gérer la sélection de fichier
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Créer une URL de prévisualisation
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Sauvegarder le profil
  const saveProfile = async () => {
    try {
      console.log('🔄 Sauvegarde du profil...');
      const token = localStorage.getItem('token');
      
      if (!token) {
        showError('Erreur d\'authentification', 'Veuillez vous reconnecter.');
        return;
      }

      const success = await updateMyProfile(profile, token);
      
      if (success) {
        console.log('✅ Profil sauvegardé avec succès');
        showSuccess('Profil mis à jour', 'Vos informations ont été mises à jour avec succès.');
        setEditMode(false);
      }
      
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde du profil:', error);
      
      let errorMessage = 'Erreur lors de la sauvegarde du profil';
      if (error.message.includes('401')) {
        errorMessage = '🔐 Session expirée. Veuillez vous reconnecter.';
      } else if (error.message.includes('403')) {
        errorMessage = '🚫 Vous n\'avez pas les permissions pour modifier ce profil.';
      } else if (error.message.includes('400')) {
        errorMessage = '📝 Données invalides. Vérifiez les informations saisies.';
      } else {
        errorMessage = `❌ ${error.message}`;
      }
      
      showError('Erreur de sauvegarde', errorMessage);
    }
  };

  // Upload de la photo
  const uploadPhoto = async () => {
    if (!selectedFile) {
      showWarning('Aucun fichier sélectionné', 'Veuillez sélectionner une photo à télécharger.');
      return;
    }

    setUploading(true);
    try {
      console.log('🔄 Upload de la photo en cours...');
      const token = localStorage.getItem('token');
      
      if (!token) {
        showError('Erreur d\'authentification', 'Veuillez vous reconnecter.');
        setUploading(false);
        return;
      }

      const response = await uploadProfilePhoto(selectedFile, token);
      
      // Extraire le photoUrl de la réponse
      const photoUrl = response.photoUrl || response;
      
      // Mettre à jour l'état local
      setProfile(prevProfile => ({
        ...prevProfile,
        photo: photoUrl
      }));

      // Notifier le composant parent
      if (onPhotoUpdate) {
        onPhotoUpdate(photoUrl);
      }

      // Nettoyer les états temporaires
      setSelectedFile(null);
      setPreviewUrl(null);
      setUploading(false);

      console.log('✅ Photo uploadée avec succès');
      showSuccess('Photo mise à jour', 'Votre photo de profil a été mise à jour avec succès.');
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'upload de la photo:', error);
      setUploading(false);
      
      let errorMessage = 'Erreur lors de l\'upload de la photo';
      if (error.message.includes('401')) {
        errorMessage = '🔐 Session expirée. Veuillez vous reconnecter.';
      } else if (error.message.includes('403')) {
        errorMessage = '🚫 Vous n\'avez pas les permissions pour modifier cette photo.';
      } else if (error.message.includes('413')) {
        errorMessage = '📏 Fichier trop volumineux. Taille maximale : 5MB.';
      } else if (error.message.includes('415')) {
        errorMessage = '🖼️ Format de fichier non supporté. Utilisez JPG, PNG ou GIF.';
      } else {
        errorMessage = `❌ ${error.message}`;
      }
      
      showError('Erreur d\'upload', errorMessage);
    }
  };

  // Supprimer la photo de profil
  const removeProfilePhoto = async () => {
    setConfirmModalData({
      title: 'Supprimer la photo',
      message: 'Êtes-vous sûr de vouloir supprimer votre photo de profil ?',
      onConfirm: async () => {
        try {
          console.log('🔄 Suppression de la photo de profil...');
          const token = localStorage.getItem('token');
          
          if (!token) {
            showError('Erreur d\'authentification', 'Veuillez vous reconnecter.');
            setShowConfirmModal(false);
            return;
          }

          await deleteProfilePhoto(token);
          
          // Mettre à jour l'état local
          setProfile(prev => ({ ...prev, photo: DEFAULT_AVATAR }));
          
          // Notifier le composant parent
          if (onPhotoUpdate) {
            onPhotoUpdate(DEFAULT_AVATAR);
          }
          
          showSuccess('Photo supprimée', 'Votre photo de profil a été supprimée avec succès.');
          setShowConfirmModal(false);
        } catch (error) {
          console.error('❌ Erreur lors de la suppression de la photo:', error);
          showError('Erreur de suppression', 'Impossible de supprimer la photo de profil.');
          setShowConfirmModal(false);
        }
      },
      onCancel: () => {
        setShowConfirmModal(false);
      }
    });
    setShowConfirmModal(true);
  };

  // Changer le mot de passe
  const changeUserPassword = async (e) => {
    e.preventDefault();
    
    // Vérifier que les mots de passe correspondent
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showError('Erreur de validation', 'Les nouveaux mots de passe ne correspondent pas.');
      return;
    }

    // Vérifier la longueur du nouveau mot de passe
    if (passwordData.newPassword.length < 6) {
      showError('Erreur de validation', 'Le nouveau mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    try {
      console.log('🔄 Changement de mot de passe en cours...');
      const token = localStorage.getItem('token');
      
      if (!token) {
        showError('Erreur d\'authentification', 'Veuillez vous reconnecter.');
        return;
      }

      await changePassword(passwordData.currentPassword, passwordData.newPassword, token);
      
      console.log('✅ Mot de passe changé avec succès');
      showSuccess('Mot de passe changé', 'Votre mot de passe a été changé avec succès.');
      
      // Réinitialiser le formulaire
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      setShowPasswordChange(false);

    } catch (error) {
      console.error('❌ Erreur lors du changement de mot de passe:', error);
      
      let errorMessage = 'Erreur lors du changement de mot de passe';
      if (error.message.includes('401')) {
        errorMessage = '🔐 Mot de passe actuel incorrect ou session expirée.';
      } else if (error.message.includes('403')) {
        errorMessage = '🚫 Vous n\'avez pas les permissions pour changer ce mot de passe.';
      } else if (error.message.includes('400')) {
        errorMessage = '📝 Données invalides. Vérifiez les mots de passe saisis.';
      } else {
        errorMessage = `❌ ${error.message}`;
      }
      
      showError('Erreur de changement de mot de passe', errorMessage);
    }
  };
  
  // Formater le rôle pour l'affichage
  const formaterRole = (role) => {
    const roles = {
      'administrateur': 'Administrateur',
      'directeur': 'Directeur',
      'conseillere': 'Conseillère',
      'comptable': 'Comptable',
      'secretaire': 'Secrétaire'
    };
    return roles[role] || role;
  };
  
  return (
    <div className="profile-container">
      <div className="profile-header">
        <h2>Mon Profil</h2>
        {!editMode && (
          <button className="btn-primary" onClick={() => setEditMode(true)}>
            Modifier le profil
          </button>
        )}
      </div>
      
      {editMode && !canEditBasicInfo() && (
        <div className="info-message">
          <i className="fas fa-info-circle"></i>
          <span>Seul l'administrateur peut modifier les informations de base (nom, prénom, email, téléphone, bureau). Vous pouvez modifier votre photo de profil et votre mot de passe.</span>
        </div>
      )}
      
      <div className="profile-content">
        <div className="profile-photo-section">
          <div className="profile-photo-container">
            <img 
              src={previewUrl || getImageUrl(profile.photo)} 
              alt="Photo de profil" 
              className="profile-photo"
            />
          </div>
          
          {editMode && (
            <div className="photo-actions">
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <label htmlFor="photo-upload" className="btn-secondary">
                Choisir une photo
              </label>
              
              {selectedFile && (
                <button 
                  className="btn-primary" 
                  onClick={uploadPhoto}
                  disabled={uploading}
                >
                  {uploading ? 'Upload en cours...' : 'Sauvegarder la photo'}
                </button>
              )}
              
              {profile.photo && profile.photo !== DEFAULT_AVATAR && (
                <button 
                  className="btn-danger" 
                  onClick={removeProfilePhoto}
                >
                  Supprimer la photo
                </button>
              )}
            </div>
          )}
        </div>

        <div className="profile-info-section">
          <div className="profile-field">
            <label>Prénom</label>
            {editMode ? (
              <input
                type="text"
                name="prenom"
                value={profile.prenom}
                onChange={handleInputChange}
                placeholder="Votre prénom"
                disabled={!canEditBasicInfo()}
                className={!canEditBasicInfo() ? 'input-disabled' : ''}
              />
            ) : (
              <span>{profile.prenom || 'Non renseigné'}</span>
            )}
          </div>

          <div className="profile-field">
            <label>Nom</label>
            {editMode ? (
              <input
                type="text"
                name="nom"
                value={profile.nom}
                onChange={handleInputChange}
                placeholder="Votre nom"
                disabled={!canEditBasicInfo()}
                className={!canEditBasicInfo() ? 'input-disabled' : ''}
              />
            ) : (
              <span>{profile.nom || 'Non renseigné'}</span>
            )}
          </div>

          <div className="profile-field">
            <label>Email</label>
            {editMode ? (
              <input
                type="email"
                name="email"
                value={profile.email}
                onChange={handleInputChange}
                placeholder="Votre email"
                disabled={!canEditBasicInfo()}
                className={!canEditBasicInfo() ? 'input-disabled' : ''}
              />
            ) : (
              <span>{profile.email || 'Non renseigné'}</span>
            )}
          </div>

          <div className="profile-field">
            <label>Téléphone</label>
            {editMode ? (
              <input
                type="tel"
                name="telephone"
                value={profile.telephone}
                onChange={handleInputChange}
                placeholder="Votre téléphone"
                disabled={!canEditBasicInfo()}
                className={!canEditBasicInfo() ? 'input-disabled' : ''}
              />
            ) : (
              <span>{profile.telephone || 'Non renseigné'}</span>
            )}
          </div>

          <div className="profile-field">
            <label>Bureau</label>
            {editMode ? (
              <input
                type="text"
                name="bureau"
                value={profile.bureau}
                onChange={handleInputChange}
                placeholder="Votre bureau"
                disabled={!canEditBasicInfo()}
                className={!canEditBasicInfo() ? 'input-disabled' : ''}
              />
            ) : (
              <span>{profile.bureau || 'Non renseigné'}</span>
            )}
          </div>

          <div className="profile-field">
            <label>Rôle</label>
            <span className={`role-badge role-${profile.role}`}>
              {formaterRole(profile.role)}
            </span>
          </div>

          <div className="profile-field">
            <label>Statut</label>
            <span className={`status-badge status-${profile.statut}`}>
              {profile.statut === 'actif' ? 'Actif' : 'Inactif'}
            </span>
          </div>

          {editMode && (
            <div className="profile-actions">
              <button className="btn-primary" onClick={saveProfile}>
                Sauvegarder
              </button>
              <button className="btn-secondary" onClick={() => setEditMode(false)}>
                Annuler
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="password-section">
        <button 
          className="btn-secondary" 
          onClick={() => setShowPasswordChange(!showPasswordChange)}
        >
          {showPasswordChange ? 'Annuler le changement' : 'Changer le mot de passe'}
        </button>

        {showPasswordChange && (
          <form onSubmit={changeUserPassword} className="password-form">
            <div className="form-group">
              <label>Mot de passe actuel</label>
              <input
                type="password"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                placeholder="Votre mot de passe actuel"
              />
            </div>

            <div className="form-group">
              <label>Nouveau mot de passe</label>
              <input
                type="password"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                placeholder="Nouveau mot de passe (min. 6 caractères)"
                minLength="6"
              />
            </div>

            <div className="form-group">
              <label>Confirmer le nouveau mot de passe</label>
              <input
                type="password"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                placeholder="Confirmer le nouveau mot de passe"
                minLength="6"
              />
            </div>

            <button type="submit" className="btn-primary">
              Changer le mot de passe
            </button>
          </form>
        )}
      </div>

      {/* Modal de confirmation personnalisée */}
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{confirmModalData.title}</h3>
            </div>
            <div className="modal-body">
              <p>{confirmModalData.message}</p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-danger" 
                onClick={confirmModalData.onConfirm}
              >
                Confirmer
              </button>
              <button 
                className="btn-secondary" 
                onClick={confirmModalData.onCancel}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;